import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, ChevronLeft, ChevronRight, Plus, User, Users, Calendar as CalendarIcon, X } from "lucide-react";

import { api } from "../api/client";
import { Button, Card, EmptyState, PageHeader, cx } from "../components/ui";
import { BookingPanel } from "../components/BookingPanel";
import { fromLocalInput, toLocalInput } from "../lib/datetime";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { formatDate } from "../lib/format";
import type { Booking, BookingStatus } from "../api/types";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

function addDays(at: Date, days: number): Date {
  const next = new Date(at);
  next.setDate(next.getDate() + days);
  return next;
}

// Monday as day 0, whatever the browser's week start says.
function weekStart(at: Date): Date {
  return addDays(at, -((at.getDay() + 6) % 7));
}

/**
 * The week at a glance: who is booked, on which day, with which provider.
 *
 * Provides both a 7-day Week View and a Provider/Staff View where bookings
 * are organized under each assigned staff member with full customer, service,
 * date, and time details.
 */
export function CalendarPage() {
  const [searchParams] = useSearchParams();
  const timeZone = useAccountTimeZone();
  const queryClient = useQueryClient();

  const [cursor, setCursor] = useState(() => {
    const param = searchParams.get("date");
    if (param) {
      const d = new Date(param.includes("T") ? param : `${param}T12:00:00Z`);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date();
  });
  const [viewMode, setViewMode] = useState<"week" | "staff">("week");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [bookingDay, setBookingDay] = useState<string | null>(null);
  const [bookingStaffId, setBookingStaffId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const monday = weekStart(cursor);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);

  const window = useMemo(() => {
    const first = toLocalInput(monday.toISOString(), timeZone).slice(0, 10);
    const last = toLocalInput(addDays(monday, 6).toISOString(), timeZone).slice(0, 10);
    return {
      from: fromLocalInput(`${first}T00:00`, timeZone),
      to: fromLocalInput(`${last}T23:59`, timeZone),
    };
  }, [monday, timeZone]);

  const query = useQuery({
    queryKey: ["bookings", window.from, window.to],
    queryFn: ({ signal }) =>
      api.bookings(
        {
          from: window.from ?? undefined,
          to: window.to ?? undefined,
          include_cancelled: "true",
          page_size: 200,
        },
        signal,
      ),
    enabled: Boolean(window.from && window.to),
    placeholderData: keepPreviousData,
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 10 * 60_000,
  });
  const products = useQuery({
    queryKey: ["product-options"],
    queryFn: ({ signal }) => api.productOptions(signal),
    staleTime: 10 * 60_000,
  });

  const byDay = useMemo(() => {
    const map = new Map<string, Booking[]>();
    for (const row of query.data?.results ?? []) {
      if (employeeFilter && row.employee !== employeeFilter) continue;
      if (serviceFilter && row.service !== serviceFilter) continue;
      const key = toLocalInput(row.starts_at, timeZone).slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), row]);
    }
    return map;
  }, [query.data, employeeFilter, serviceFilter, timeZone]);

  const onCreated = (booking?: Booking) => {
    setSuccessMessage("Booking created successfully.");
    void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    void queryClient.invalidateQueries({ queryKey: ["bookings-list"] });
    if (booking?.starts_at) {
      const bookingDate = new Date(booking.starts_at);
      if (bookingDate < monday || bookingDate > addDays(monday, 6)) {
        setCursor(bookingDate);
      }
    }
  };

  const todayKey = toLocalInput(new Date().toISOString(), timeZone).slice(0, 10);
  const selectedDayKey = toLocalInput(cursor.toISOString(), timeZone).slice(0, 10);
  const none = (query.data?.results.length ?? 0) === 0;

  // For staff view: group bookings on selectedDayKey by employee
  const bookingsForSelectedDay = byDay.get(selectedDayKey) ?? [];
  const staffList = employees.data ?? [];

  return (
    <>
      <PageHeader
        title="Calendar"
        subtitle={
          query.data
            ? `${query.data.results.length} booking${query.data.results.length === 1 ? "" : "s"} this week`
            : "The week's bookings, per day and per provider"
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-300 bg-slate-100 p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setViewMode("week")}
                className={cx(
                  "inline-flex items-center gap-1 rounded px-2.5 py-1 transition",
                  viewMode === "week"
                    ? "bg-white text-slate-900 shadow-xs font-semibold"
                    : "text-slate-600 hover:text-slate-900",
                )}
              >
                <CalendarIcon size={13} /> Week
              </button>
              <button
                type="button"
                onClick={() => setViewMode("staff")}
                className={cx(
                  "inline-flex items-center gap-1 rounded px-2.5 py-1 transition",
                  viewMode === "staff"
                    ? "bg-white text-slate-900 shadow-xs font-semibold"
                    : "text-slate-600 hover:text-slate-900",
                )}
              >
                <Users size={13} /> By Staff
              </button>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCursor(addDays(cursor, viewMode === "week" ? -7 : -1))}
                aria-label={viewMode === "week" ? "Previous week" : "Previous day"}
                className="rounded-md p-1.5 text-slate-500 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                onClick={() => setCursor(new Date())}
                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              >
                Today
              </button>
              <button
                onClick={() => setCursor(addDays(cursor, viewMode === "week" ? 7 : 1))}
                aria-label={viewMode === "week" ? "Next week" : "Next day"}
                className="rounded-md p-1.5 text-slate-500 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              >
                <ChevronRight size={15} />
              </button>
            </div>

            <select
              value={employeeFilter}
              onChange={(event) => setEmployeeFilter(event.target.value)}
              aria-label="Filter by provider"
              className="rounded-md border-0 bg-white px-2 py-1.5 text-xs ring-1 ring-inset ring-slate-300"
            >
              <option value="">All providers</option>
              {staffList.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.display_name}
                </option>
              ))}
            </select>

            <select
              value={serviceFilter}
              onChange={(event) => setServiceFilter(event.target.value)}
              aria-label="Filter by service"
              className="rounded-md border-0 bg-white px-2 py-1.5 text-xs ring-1 ring-inset ring-slate-300"
            >
              <option value="">All services</option>
              {(products.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setBookingDay(selectedDayKey);
                setBookingStaffId(employeeFilter || null);
              }}
              className="inline-flex items-center gap-1.5"
            >
              <Plus size={14} /> New booking
            </Button>
          </div>
        }
      />

      {successMessage ? (
        <div
          role="status"
          className="mb-4 flex items-center justify-between rounded-md bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20 shadow-xs"
        >
          <span className="flex items-center gap-2">
            <Check size={16} className="text-emerald-600 shrink-0" />
            {successMessage}
          </span>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            className="text-emerald-700 hover:text-emerald-900"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      {bookingDay ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <BookingPanel
            preselect={{ date: bookingDay, employeeId: bookingStaffId ?? undefined }}
            onClose={() => {
              setBookingDay(null);
              setBookingStaffId(null);
            }}
            onCreated={onCreated}
          />
        </div>
      ) : null}

      {query.isPending ? (
        <Card>
          <p className="py-10 text-sm text-slate-400">Loading the calendar…</p>
        </Card>
      ) : query.isError ? (
        <Card>
          <EmptyState title="Could not load the calendar" body="Please try again." />
        </Card>
      ) : viewMode === "staff" ? (
        /* Staff Columns View: displays bookings under each assigned staff member */
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">
              Staff Schedule · {formatDate(fromLocalInput(`${selectedDayKey}T00:00`, timeZone), timeZone)}
            </h2>
            <span className="text-xs text-slate-500">
              {bookingsForSelectedDay.length} appointment{bookingsForSelectedDay.length === 1 ? "" : "s"} on this date
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {staffList.map((emp) => {
              const staffBookings = bookingsForSelectedDay.filter((b) => b.employee === emp.id);
              return (
                <div
                  key={emp.id}
                  className="flex flex-col rounded-lg border border-slate-200 bg-white p-3 shadow-xs"
                >
                  <div className="mb-2.5 flex items-center gap-2 border-b border-slate-100 pb-2">
                    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                      {emp.display_name?.charAt(0) || "S"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-slate-900" title={emp.display_name}>
                        {emp.display_name}
                      </p>
                      <p className="truncate text-[10px] text-slate-500">
                        {emp.designation || "Staff Member"}
                      </p>
                    </div>
                  </div>

                  <div className="flex-1 space-y-2">
                    {staffBookings.length === 0 ? (
                      <p className="py-6 text-center text-xs text-slate-400">No bookings</p>
                    ) : (
                      staffBookings.map((row) => (
                        <BookingCard key={row.id} row={row} timeZone={timeZone} showStaff={false} />
                      ))
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setBookingDay(selectedDayKey);
                      setBookingStaffId(emp.id);
                    }}
                    className="mt-3 flex items-center justify-center gap-1 rounded border border-dashed border-slate-300 py-1.5 text-[11px] font-medium text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <Plus size={12} /> Book with {emp.display_name.split(" ")[0]}
                  </button>
                </div>
              );
            })}

            {/* Unassigned column if any unassigned bookings exist */}
            {bookingsForSelectedDay.some((b) => !b.employee) ? (
              <div className="flex flex-col rounded-lg border border-dashed border-amber-300 bg-amber-50/30 p-3">
                <div className="mb-2.5 flex items-center gap-2 border-b border-amber-200/60 pb-2">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-800">
                    ?
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-amber-900">Unassigned Staff</p>
                    <p className="truncate text-[10px] text-amber-700">Needs staff assignment</p>
                  </div>
                </div>

                <div className="flex-1 space-y-2">
                  {bookingsForSelectedDay
                    .filter((b) => !b.employee)
                    .map((row) => (
                      <BookingCard key={row.id} row={row} timeZone={timeZone} showStaff={false} />
                    ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        /* Week View: 7 day columns */
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
          {days.map((day) => {
            const key = toLocalInput(day.toISOString(), timeZone).slice(0, 10);
            const today = key === todayKey;
            const rows = byDay.get(key) ?? [];
            return (
              <div
                key={key}
                className={cx(
                  "flex flex-col rounded-lg border p-2",
                  today ? "border-slate-900 bg-white ring-1 ring-slate-900/10" : "border-slate-200 bg-slate-50/40",
                )}
              >
                <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-1">
                  <p className={cx("text-[11px] font-semibold", today ? "text-slate-900" : "text-slate-600")}>
                    {DAY_NAMES[day.getDay() % 7]}
                    <span className="text-slate-400 font-normal"> {day.getDate()}</span>
                  </p>
                  {today ? (
                    <span className="rounded bg-slate-900 px-1 py-0.2 text-[9px] font-semibold text-white">
                      Today
                    </span>
                  ) : null}
                </div>

                <div className="flex-1 space-y-1.5">
                  {rows.length === 0 ? (
                    <p className="py-4 text-center text-[10px] text-slate-300">—</p>
                  ) : (
                    rows.map((row) => <BookingCard key={row.id} row={row} timeZone={timeZone} showStaff={true} />)
                  )}
                </div>

                <button
                  onClick={() => {
                    setBookingDay(key);
                    setBookingStaffId(employeeFilter || null);
                  }}
                  className="mt-2 flex items-center justify-center gap-1 rounded border border-slate-200 bg-white py-1 text-[10px] font-medium text-slate-600 shadow-2xs hover:bg-slate-50 hover:text-slate-900"
                >
                  <Plus size={11} className="inline" /> Book slot
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!query.isPending && none ? (
        <Card className="mt-4">
          <EmptyState
            icon={<BookOpen size={28} />}
            title="Nothing booked this week"
            body="Mark a lead as Won with an agreed time and its slot lands here. Or pick a day above and reserve one now."
          />
        </Card>
      ) : null}
    </>
  );
}

function BookingCard({
  row,
  timeZone,
  showStaff = true,
}: {
  row: Booking;
  timeZone: string | undefined;
  showStaff?: boolean;
}) {
  const cancelled = row.status === "cancelled";
  const startStr = toLocalInput(row.starts_at, timeZone).slice(11, 16);
  const endStr = row.ends_at ? toLocalInput(row.ends_at, timeZone).slice(11, 16) : "";
  const timeDisplay = endStr ? `${startStr} - ${endStr}` : startStr;

  return (
    <div
      className={cx(
        "rounded-md border p-2 text-[11px] leading-tight transition-shadow shadow-xs",
        cancelled ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-white text-slate-900 border-sky-200 hover:border-sky-400",
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <span className={cx("font-semibold text-xs tabular-nums text-sky-800", cancelled && "line-through text-slate-400")}>
          {timeDisplay}
        </span>
        {statusBadge(row.status, row.status_display)}
      </div>

      <p className="mt-1 font-medium text-slate-900 truncate" title={row.contact_name || "Blocked slot"}>
        {row.contact_name || "Blocked slot"}
      </p>

      <p className="truncate text-[10px] text-slate-500 font-normal" title={row.service_name || "Service"}>
        {row.service_name || "Service"}
      </p>

      {showStaff ? (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-600 bg-slate-50 rounded px-1.5 py-0.5 border border-slate-100">
          <User size={10} className="text-slate-400 shrink-0" />
          <span className="text-slate-500 font-normal">Staff:</span>
          <span className="font-medium text-slate-800 truncate">{row.employee_name || "Unassigned"}</span>
        </div>
      ) : null}
    </div>
  );
}

function statusBadge(status: BookingStatus, label: string) {
  const tone: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 ring-amber-600/20",
    confirmed: "bg-sky-50 text-sky-700 ring-sky-600/20",
    checked_in: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    in_progress: "bg-violet-50 text-violet-700 ring-violet-600/20",
    completed: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    no_show: "bg-rose-50 text-rose-700 ring-rose-600/20",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-400/20",
  };
  return (
    <span className={cx("inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium ring-1 ring-inset capitalize", tone[status] ?? tone.cancelled)}>
      {label || status}
    </span>
  );
}